/**
 * «Pagar al INSS, al INATEC y a la DGI» (2026-10-31): what the runs withheld and owe — each person's
 * INSS and the shop's, the INATEC, the income tax withheld — paid as the institutions bill it
 * (`cotizaciones.pagadas`). Nothing could pay them: the books owed them for ever, and what the shop
 * paid the INSS went in «Gastos», where the shop's share of it was spent a second time.
 */
import { useState } from 'react';
import { formatMoney, hoy, mesAnterior, newId, parseMoney, type Centavos, type CuentaBancoCordobas, type Settings } from '@abastece/shared';
import type { PagoCotizaciones, Planilla } from '@abastece/core';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { cotizacionesPorPagar } from '../lib/planilla.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

type Concepto = 'inss' | 'inatec' | 'ir';
const CONCEPTOS: readonly Concepto[] = ['inss', 'inatec', 'ir'];
const ETIQUETA: Record<Concepto, Clave> = { inss: 'planilla.cotizacion.inss', inatec: 'planilla.cotizacion.inatec', ir: 'planilla.cotizacion.ir' };

/** The month a bill most likely is: the last one before this one that runs owe for, or the last any run is of. */
function mesSugerido(corridas: readonly Planilla[]): string {
  const anterior = mesAnterior(hoy().slice(0, 7));
  const meses = [...new Set(corridas.map((p) => p.periodo))].sort();
  return meses.filter((m) => m <= anterior).at(-1) ?? meses.at(-1) ?? anterior;
}

export function PagarCotizaciones({
  corridas,
  pagos,
  config,
  onCerrar,
}: {
  corridas: readonly Planilla[];
  pagos: readonly PagoCotizaciones[];
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  const [periodo, setPeriodo] = useState(() => mesSugerido(corridas));
  /** What was typed of each, as the bill says it; until then, what the runs up to the month owe. */
  const [escritos, setEscritos] = useState<Partial<Record<Concepto, string>>>({});
  const [fecha, setFecha] = useState(hoy());
  const [formaPago, setFormaPago] = useState<'efectivo_cordobas' | 'transferencia' | 'billetera'>('transferencia');
  /** The córdoba account a payment by transfer goes out of, once picked. */
  const [cuenta, setCuenta] = useState<CuentaBancoCordobas | null>(null);
  const [referencia, setReferencia] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const debe = cotizacionesPorPagar(corridas, pagos, periodo);
  const texto = (c: Concepto) => escritos[c] ?? (Math.max(0, debe[c]) / 100).toFixed(2);
  /** Blank is nothing; typed and unreadable is said, never paid as nothing. */
  const monto = (c: Concepto) => (texto(c).trim() === '' ? 0 : parseMoney(texto(c)));
  const montos = Object.fromEntries(CONCEPTOS.map((c) => [c, monto(c)])) as Record<Concepto, number | null>;
  const legibles = CONCEPTOS.every((c) => montos[c] !== null && montos[c]! >= 0);
  const total = legibles ? CONCEPTOS.reduce((a, c) => a + montos[c]!, 0) : 0;
  const fechaBien = /^\d{4}-\d{2}-\d{2}$/.test(fecha) && fecha <= hoy();
  const lista = /^\d{4}-\d{2}$/.test(periodo) && legibles && total > 0 && fechaBien;

  async function pagar() {
    if (!lista) return;
    setError(null);
    setTrabajando(true);
    try {
      const { rechazo } = await emitirYVer('cotizaciones.pagadas', {
        pagoId: newId(),
        fecha,
        periodo,
        inss: montos.inss!,
        inatec: montos.inatec!,
        ir: montos.ir!,
        formaPago,
        ...(formaPago === 'transferencia' ? { cuenta: cuentaEnCordobas(config, cuenta) } : {}),
        ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
      });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t('planilla.pagarCotizaciones')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-rose-400">{error}</p>}
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-slate-400">{t('comun.total')}</span>
            <span className="tabular text-lg font-semibold" data-cotizaciones-total>
              {formatMoney(total as Centavos)}
            </span>
          </div>
          <button type="button" className="btn-primario" disabled={trabajando || !lista} onClick={() => void pagar()}>
            <Ico icono="pagar" />
            {t('planilla.registrarCotizaciones')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-pagar-cotizaciones>
        <Campo
          etiqueta={t('planilla.mesQueSePaga')}
          type="month"
          value={periodo}
          onChange={(e) => {
            if (!e.target.value) return;
            setPeriodo(e.target.value);
            setEscritos({});
          }}
          ayuda={t('planilla.cotizacionesAyuda')}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {CONCEPTOS.map((c) => (
            <Campo
              key={c}
              etiqueta={t(ETIQUETA[c])}
              inputMode="decimal"
              className="tabular"
              value={texto(c)}
              onChange={(e) => setEscritos((x) => ({ ...x, [c]: e.target.value }))}
              autoComplete="off"
              data-cotizacion={c}
              error={montos[c] === null || montos[c]! < 0 ? t('caja.montoInvalido') : null}
              ayuda={t('planilla.seDebe', { monto: formatMoney(debe[c]) })}
            />
          ))}
        </div>
        <Campo
          etiqueta={t('planilla.sePagoEl')}
          type="date"
          value={fecha}
          max={hoy()}
          onChange={(e) => setFecha(e.target.value)}
          error={fechaBien ? null : t('gastoPaso.fechaFutura')}
        />
        <Selector etiqueta={t('compra.formaPago')} value={formaPago} onChange={(e) => setFormaPago(e.target.value as typeof formaPago)}>
          <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
          <option value="transferencia">{t('pago.transferencia')}</option>
          <option value="billetera">{t('gastos.billeteraMovil')}</option>
        </Selector>
        {formaPago === 'transferencia' && (
          <ElegirCuenta valor={cuentaEnCordobas(config, cuenta)} onCambiar={(c) => setCuenta(c as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />
        )}
        {formaPago === 'efectivo_cordobas' && <p className="text-xs text-slate-500">{t('planilla.pagoDeEfectivoGeneral')}</p>}
        <Campo etiqueta={t('comun.referencia')} value={referencia} onChange={(e) => setReferencia(e.target.value)} maxLength={80} autoComplete="off" />
      </div>
    </Modal>
  );
}
